import { useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";

type LegalSection = {
  title: string;
  content?: string;
  subsections?: ReadonlyArray<{ subtitle: string; text: string }>;
};

export default function Terms() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = t("legal.terms.sections", {
    returnObjects: true,
  }) as ReadonlyArray<LegalSection>;

  return (
    <Layout>
      <div className="min-h-screen bg-white text-black">
        {/* Hero Section */}
        <section className="bg-black text-white py-16 md:py-24 border-b-4 border-white">
          <div className="container">
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="inline-block bg-white text-black px-4 py-1 font-mono text-xs tracking-widest mb-6">
                {t("legal.terms.hero.label")}
              </div>
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.9] mb-6">
                {t("legal.terms.hero.title")}
              </h1>
              <p className="text-white/70 text-lg md:text-xl max-w-2xl leading-relaxed">
                {t("legal.terms.hero.subtitle")}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 md:py-24">
          <div className="container max-w-3xl">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-12"
            >
              {sections.map((section, idx) => (
                <motion.div
                  key={idx}
                  initial={{ y: 40, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  viewport={{ once: true, margin: "-100px" }}
                  className="border-l-4 border-black pl-6"
                >
                  <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight mb-4">
                    {section.title}
                  </h2>

                  {section.content && (
                    <p className="text-black/70 text-base leading-relaxed whitespace-pre-wrap mb-6">
                      {section.content}
                    </p>
                  )}

                  {section.subsections && (
                    <div className="space-y-6">
                      {section.subsections.map((subsection, subIdx) => (
                        <div key={subIdx}>
                          <h3 className="font-heading font-semibold text-lg tracking-tight mb-3">
                            {subsection.subtitle}
                          </h3>
                          <p className="text-black/70 text-base leading-relaxed whitespace-pre-wrap">
                            {subsection.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}

              {/* Footer */}
              <motion.div
                initial={{ y: 40, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="border-t-4 border-black pt-8 mt-12"
              >
                <p className="text-black/60 text-sm font-mono tracking-widest mb-4">
                  {t("legal.terms.footer.copyright")}
                </p>
                <p className="text-black/60 text-sm font-mono">
                  {t("legal.terms.footer.version")}
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-black text-white py-16 md:py-20 border-t-4 border-white">
          <div className="container">
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="max-w-2xl"
            >
              <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight mb-4">
                {t("legal.terms.cta.title")}
              </h2>
              <p className="text-white/70 text-lg mb-6 leading-relaxed">
                {t("legal.terms.cta.body")}
              </p>
              <a
                href={t("legal.terms.cta.mailto")}
                className="inline-block bg-white text-black px-6 py-3 font-heading font-semibold tracking-wider border-3 border-white hover:bg-black hover:text-white transition-colors duration-200"
              >
                {t("legal.terms.cta.button")}
              </a>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
